import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { Search, Filter, Save, X, Edit2, Printer, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import '../admin/admin_member_list.css';

const AdminMemberList = () => {
    const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); 
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [memberTypeFilter, setMemberTypeFilter] = useState('all');

  // 🟢 NEW STATE: Sorting & Custom Filters
  const [sortField, setSortField] = useState('none'); // 'caNo' | 'parmNo' | 'joinedDate' | 'membershipExpiry'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  const [caFilter, setCaFilter] = useState('all'); // 'all' | 'has_ca' | 'no_ca'

  const handlePrint = () => {
    const tableContent = document.querySelector('.printable-target-list').innerHTML;
    const printWindow = window.open('', '_blank', 'height=600,width=900');
    
    printWindow.document.write(`
        <html>
        <head>
            <title>PArM Membership List Export</title>
            <style>
            body { font-family: 'Poppins', sans-serif; padding: 10px; font-size: 9px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 9px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px 8px; text-align: left; }
            th { background-color: #f1f5f9 !important; font-weight: bold; }
            .badge-parm-no {font-weight: bold; }
            .table-status-pill { text-transform: uppercase; font-size: 9px; font-weight: bold; }
            th:last-child, td:last-child { display: none !important; }
            </style>
        </head>
        <body>
            <h2 style="margin-bottom: 5px;">PArM Membership List Report</h2>
            <p style="margin-top: 0; color: #475569; font-size: 9px;">Generated on: ${new Date().toLocaleDateString('en-MY')}</p>
            <hr style="border: 0; border-top: 1px solid #cbd5e1; margin-bottom: 20px;" />
            <table>
            ${tableContent}
            </table>
        </body>
        </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "registrations")); 
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMembers(data);
      setFilteredMembers(data);
    } catch (err) {
      console.error("Error fetching members:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    // 1. Filter out non-verified members
    let result = members.filter(member => member.status === 'verified');
    
    // 2. Apply Active List rule
    if (activeFilter === 'active') {
        const currentYear = new Date().getFullYear();

        result = result.filter(member => {
          const type = member.memberType || member.membershipType || '';
          const isExempt = type.includes('Lifetime Members') || type.includes('Ahli Seumur Hidup') || 
                          type.includes('Corporate Members') || type.includes('Ahli Korporat');

          if (isExempt) return true;

          let expiryYear = null;
          if (member.membershipExpiry?.seconds) {
              expiryYear = new Date(member.membershipExpiry.seconds * 1000).getFullYear();
          } else if (member.membershipExpiry) {
              expiryYear = new Date(String(member.membershipExpiry).replace(/\sat\s/i, ' ')).getFullYear();
          }

          if (!expiryYear || isNaN(expiryYear)) return false;

          const yearsOwed = currentYear - expiryYear;
          return yearsOwed <= 3;
        });
    }

    if (caFilter === 'has_ca') {
      result = result.filter(m => m.caNo && m.caNo.trim() !== '' && m.caNo !== 'N/A');
    } else if (caFilter === 'no_ca') {
      result = result.filter(m => !m.caNo || m.caNo.trim() === '' || m.caNo === 'N/A');
    }

    const typeKeywords = {
      Ordinary: ['ordinary members', 'ahli biasa'],
      Professional: ['professional members', 'ahli profesional'],
      Corporate: ['corporate members', 'ahli korporat'],
      Veteran: ['veteran members', 'ahli veteran'],
      Student: ['student members', 'ahli pelajar'],
      Lifetime: ['lifetime members', 'ahli seumur hidup']
    };

    if (memberTypeFilter !== 'all') {
      result = result.filter(member => {
        const type = (member.memberType || member.membershipType || '').toLowerCase();
        const keywords = typeKeywords[memberTypeFilter] || [memberTypeFilter.toLowerCase()];
        return keywords.some(kw => type.includes(kw));
      });
    }

    // 4. Search filter
    if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        result = result.filter(member => 
          member.name?.toLowerCase().includes(query) ||
          member.icNo?.toLowerCase().includes(query) ||
          member.email?.toLowerCase().includes(query) ||
          member.parmNo?.toLowerCase().includes(query) ||
          member.caNo?.toLowerCase().includes(query) ||
          member.memberType?.toLowerCase().includes(query)
        );
    }

    // 5. 🟢 Apply Dynamic Sorting
    if (sortField !== 'none') {
      result.sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        // Parse Date/Timestamp objects for accurate date comparison
        if (sortField === 'joinedDate' || sortField === 'membershipExpiry') {
          valA = valA?.seconds ? valA.seconds * 1000 : new Date(valA || 0).getTime();
          valB = valB?.seconds ? valB.seconds * 1000 : new Date(valB || 0).getTime();
        } else {
          // Standardize string comparison for CA No and PArM No
          valA = (valA || '').toString().toLowerCase();
          valB = (valB || '').toString().toLowerCase();
        }

        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    setFilteredMembers(result);
  }, [searchQuery, activeFilter, caFilter, memberTypeFilter, sortField, sortOrder, members]);

  // Helper formatting routines
  const formatTimestampToDisplay = (timestamp) => {
    if (!timestamp || (!timestamp.seconds && isNaN(new Date(timestamp)))) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatTimestampToInputValue = (timestamp) => {
    if (!timestamp || (!timestamp.seconds && isNaN(new Date(timestamp)))) return '';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const convertInputValueToTimestamp = (dateString) => {
    if (!dateString) return null;
    return Timestamp.fromDate(new Date(dateString));
  };

  const handleEditClick = (member) => {
    setEditingId(member.id);
    setEditFormData({
      name: member.name || '',
      email: member.email || '',
      memberType: member.memberType || '',
      parmNo: member.parmNo || '',
      caNo: member.caNo || '',
      status: member.status || '',
      joinedDate: formatTimestampToInputValue(member.joinedDate),
      lastPaymentDate: formatTimestampToInputValue(member.lastPaymentDate),
      membershipExpiry: formatTimestampToInputValue(member.membershipExpiry),
    });
  };

  const handleInputChange = (e, field) => {
    setEditFormData({
      ...editFormData,
      [field]: e.target.value
    });
  };

  const handleSaveClick = async (id) => {
    try {
      const updatedDataForFirebase = {
        ...editFormData,
        joinedDate: convertInputValueToTimestamp(editFormData.joinedDate),
        lastPaymentDate: convertInputValueToTimestamp(editFormData.lastPaymentDate),
        membershipExpiry: convertInputValueToTimestamp(editFormData.membershipExpiry)
      };

      const docRef = doc(db, "registrations", id);
      await updateDoc(docRef, updatedDataForFirebase);
      
      setMembers(members.map(m => m.id === id ? { ...m, ...updatedDataForFirebase } : m));
      setEditingId(null);
    } catch (err) {
      console.error("Error saving record configurations:", err);
      alert("Failed to save data changes.");
    }
  };

  // Toggle sort direction or field
  const handleSortChange = (field) => {
    if (sortField === field) {
      if (sortOrder === 'asc') setSortOrder('desc');
      else {
        setSortField('none');
        setSortOrder('asc');
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

const getAnnualFee = (memberType) => {
  const type = (memberType || '').toLowerCase();
  
  if (type.includes('corporate') || type.includes('ahli korporat')) return 0;
  if (type.includes('lifetime') || type.includes('seumur hidup')) return 0;
  
  if (type.includes('professional') || type.includes('ordinary') || type.includes('biasa')) return 100;
  if (type.includes('veteran') || type.includes('student') || type.includes('pelajar')) return 25;
  
  return 100; // Default fallback fee
};

const calculateOutstanding = (member) => {
  const type = member.memberType || member.membershipType || '';
  
  if (
    type.includes('Lifetime') || type.includes('Ahli Seumur Hidup') ||
    type.includes('Corporate') || type.includes('Ahli Korporat')
  ) {
    return { yearsOwed: 0, amount: 0 };
  }

  let expiryYear = null;
  if (member.membershipExpiry?.seconds) {
    expiryYear = new Date(member.membershipExpiry.seconds * 1000).getUTCFullYear();
  } else if (member.membershipExpiry) {
    const parsed = new Date(String(member.membershipExpiry).replace(/\sat\s/i, ' '));
    expiryYear = !isNaN(parsed.getTime()) ? parsed.getUTCFullYear() : null;
  }

  if (!expiryYear) return { yearsOwed: 0, amount: 0 };

  const currentYear = new Date().getFullYear(); // 2026
  const yearsOwed = currentYear - expiryYear;

  if (yearsOwed <= 0) return { yearsOwed: 0, amount: 0 };

  const annualFee = getAnnualFee(type);
  return { 
    yearsOwed, 
    amount: yearsOwed * annualFee 
  };
};

  if (loading) return <div className="admin-loader">Loading PArM Registry Assets...</div>;

  return (
    <div className="admin-member-page">
      <div className="admin-member-container">
        <header className="page-header">
          <h2>PArM Master Membership List</h2>
          <p>Search, filter, and modify professional profiles across active clusters.</p>
        </header>

        <div className="toolbar-wrapper">
          <div className="search-bar-box">
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Search by name, email, PArM number..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-buttons-group">
            {/* CA No Filter Selector */}
            <select 
              className="admin-select-filter"
              value={caFilter} 
              onChange={(e) => setCaFilter(e.target.value)}
            >
              <option value="all">CA No: All</option>
              <option value="has_ca">Has CA No</option>
              <option value="no_ca">No CA No</option>
            </select>

            <select 
              className="admin-select-filter"
              value={memberTypeFilter} 
              onChange={(e) => setMemberTypeFilter(e.target.value)}
            >
              <option value="all">Member Type: All</option>
              <option value="Ordinary">Ordinary Members</option>
              <option value="Professional">Professional Members</option>
              <option value="Corporate">Corporate Members</option>
              <option value="Veteran">Veteran Members</option>
              <option value="Student">Student Members</option>
              <option value="Lifetime">Lifetime Members</option>
            </select>

            {/* Sort Dropdown Controls */}
            <select 
              className="admin-select-filter"
              value={sortField} 
              onChange={(e) => { setSortField(e.target.value); setSortOrder('asc'); }}
            >
              <option value="none">Sort By: Default</option>
              <option value="caNo">CA No</option>
              <option value="parmNo">PArM No</option>
              <option value="joinedDate">Joined Date</option>
              <option value="membershipExpiry">Membership Expiry</option>
            </select>

            {sortField !== 'none' && (
              <button 
                className="filter-btn sort-order-btn"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title="Toggle Sort Order"
              >
                {sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                {sortOrder.toUpperCase()}
              </button>
            )}

            <button 
              className={`filter-btn ${activeFilter === 'all' && !isEditMode ? 'active' : ''}`}
              onClick={() => { setActiveFilter('all'); setIsEditMode(false); setEditingId(null); }}
            >
              All List
            </button>

            <button 
              className={`filter-btn ${activeFilter === 'active' && !isEditMode ? 'active' : ''}`}
              onClick={() => { setActiveFilter('active'); setIsEditMode(false); setEditingId(null); }}
            >
              Active List
            </button>

            <button 
              className={`filter-btn edit-toggle-btn ${isEditMode ? 'active-edit' : ''}`}
              onClick={() => { setIsEditMode(!isEditMode); setActiveFilter('all'); }}
            >
              <Edit2 size={14} style={{ marginRight: '6px' }} />
              {isEditMode ? "Exit Edit Mode" : "Edit List"}
            </button>
            
            <button className="filter-btn print-btn" onClick={handlePrint}>
              <Printer size={14} style={{ marginRight: '6px' }} />
              Print List
            </button>
          </div>
        </div>

        <div className="table-responsive-wrapper printable-target-list">
          <table className="member-data-table">
            <thead>
              <tr>
                {/* <th>PArM No</th> */}
                <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('parmNo')}>
                  PArM No {sortField === 'parmNo' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                {/* <th>CA No</th> */}
                <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('caNo')}>
                  CA No {sortField === 'caNo' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Full Name</th>
                <th>IC No</th>
                <th>Email</th>
                <th>phone</th>
                <th>Membership Type</th>
                {/* <th>Joined Date</th> */}
                <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('joinedDate')}>
                  Joined Date {sortField === 'joinedDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Last Payment</th>
                {/* <th>Expiry Date</th> */}
                <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('membershipExpiry')}>
                  Expiry Date {sortField === 'membershipExpiry' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>

                <th style={{ cursor: 'pointer' }} onClick={() => handleSortChange('outstanding')}>
          Outstanding (RM) {sortField === 'outstanding' && (sortOrder === 'asc' ? '↑' : '↓')}
        </th>

                <th>Status</th>
                {isEditMode && <th style={{ textAlign: 'center' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member) => {
                const arrears = calculateOutstanding(member);

                return (
                <tr key={member.id} className={editingId === member.id ? "row-editing" : ""}>
                  
                  {/* PArM No */}
                  <td>
                    {editingId === member.id ? (
                      <input type="text" className="table-inline-input" value={editFormData.parmNo} onChange={(e) => handleInputChange(e, 'parmNo')} />
                    ) : (
                      <span className="badge-parm-no">{member.parmNo || 'N/A'}</span>
                    )}
                  </td>

                  {/* CA No */}
                  <td>
                    {editingId === member.id ? (
                      <input type="text" className="table-inline-input" value={editFormData.caNo} onChange={(e) => handleInputChange(e, 'caNo')} />
                    ) : (
                      <span >{member.caNo || 'N/A'}</span>
                    )}
                  </td>

                  {/* Full Name */}
                  <td className="text-bold">
                    {
                      member.name
                    }
                  </td>

                  {/* ic No */}
                  <td>
                    {
                      member.icNo
                    }
                  </td>

                  {/* Email */}
                  <td>
                    {editingId === member.id ? (
                      <input type="text" className="table-inline-input" value={editFormData.email} onChange={(e) => handleInputChange(e, 'email')} />
                    ) : (
                      member.email
                    )}
                  </td>

                  {/* Phone no */}
                  <td>
                    {editingId === member.id ? (
                      <input type="text" className="table-inline-input" value={editFormData.email} onChange={(e) => handleInputChange(e, 'email')} />
                    ) : (
                      member.phone
                    )}
                  </td>

                  {/* Membership Type */}
                  <td>
                    {editingId === member.id ? (
                      <select className="table-inline-select" value={editFormData.memberType} onChange={(e) => handleInputChange(e, 'memberType')}>
                        <option value="Ordinary Members">Ordinary Members</option>
                        <option value="Professional Members">Professional Members</option>
                        <option value="Corporate Members">Corporate Members</option>
                        <option value="Veteran Members">Veteran Members</option>
                        <option value="Student Members">Student Members</option>
                        <option value="Lifetime Members">Lifetime Members</option>
                      </select>
                    ) : (
                      member.memberType || 'N/A'
                    )}
                  </td>

                  {/* 🟢 Column: Joined Date */}
                  <td>
                    {editingId === member.id ? (
                      <input type="date" className="table-inline-input" value={editFormData.joinedDate} onChange={(e) => handleInputChange(e, 'joinedDate')} />
                    ) : (
                      <span className="date-text-node">{formatTimestampToDisplay(member.joinedDate)}</span>
                    )}
                  </td>

                  {/* 🟢 Column: Last Payment Date */}
                  <td>
                    {editingId === member.id ? (
                      <input type="date" className="table-inline-input" value={editFormData.lastPaymentDate} onChange={(e) => handleInputChange(e, 'lastPaymentDate')} />
                    ) : (
                      <span className="date-text-node">{formatTimestampToDisplay(member.lastPaymentDate)}</span>
                    )}
                  </td>

                  {/* 🟢 Column: Membership Expiry */}
                  <td>
                    {editingId === member.id ? (
                      <input type="date" className="table-inline-input" value={editFormData.membershipExpiry} onChange={(e) => handleInputChange(e, 'membershipExpiry')} />
                    ) : (
                      <span className="date-text-node">{formatTimestampToDisplay(member.membershipExpiry)}</span>
                    )}
                  </td>

                  <td>
                  {arrears.amount > 0 ? (
                    <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
                      RM {arrears.amount.toLocaleString()} 
                      <span style={{ fontSize: '0.7rem', display: 'block', color: '#64748b', fontWeight: 'normal' }}>
                        ({arrears.yearsOwed} yr{arrears.yearsOwed > 1 ? 's' : ''} overdue)
                      </span>
                    </span>
                  ) : (
                    <span style={{ color: '#22c55e', fontWeight: '600' }}>Up to date</span>
                  )}
                </td>

                  {/* Status */}
                  <td>
                    {
                    // editingId === member.id ? (
                    //     <select className="table-inline-select" value={editFormData.status} onChange={(e) => handleInputChange(e, 'status')}>
                    //     <option value="pending">Pending</option>
                    //     <option value="pending_payment">Pending Payment</option>
                    //     <option value="verified">Verified</option>
                    //     </select>
                    // ) : (
                        <span className={`table-status-pill ${member.status === 'verified' ? 'status-active' : 'status-inactive'}`}>
                        {/* 🟢 FIXED: Changed 'Active' string literal text to 'Verified' */}
                        {member.status === 'verified' ? 'Verified' : member.status || 'Inactive'}
                        </span>
                    // )
                    }
                    </td>

                  {/* Actions Column */}
                  {isEditMode && (
                    <td style={{ textAlign: 'center' }}>
                      {editingId === member.id ? (
                        <div className="table-action-buttons">
                          <button className="act-btn btn-save" onClick={() => handleSaveClick(member.id)}><Save size={14} /></button>
                          <button className="act-btn btn-cancel" onClick={() => setEditingId(null)}><X size={14} /></button>
                        </div>
                      ) : (
                        <button className="act-btn btn-edit" onClick={() => handleEditClick(member)}><Edit2 size={14} /></button>
                      )}
                    </td>
                  )}

                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminMemberList;